/**
 * Feedback Selection Overlay
 * Shows highlight over hovered element and confirmation dialog
 */

import React from 'react';
import { Check, X, RotateCcw, MessageSquarePlus } from 'lucide-react';
import { useFeedback } from '../../contexts/FeedbackContext';

const FeedbackSelectionOverlay = () => {
  const {
    selectionMode,
    hoveredElement,
    showConfirmation,
    confirmationPosition,
    confirmElementSelection,
    reselectElement,
    cancelSelectionMode
  } = useFeedback();

  if (!selectionMode) return null;

  return (
    <>
      {/* Element Highlight */}
      {hoveredElement && hoveredElement.rect && (
        <div
          className="feedback-highlight fixed pointer-events-none z-[9998] border-4 border-purple-500 bg-purple-500/10 rounded transition-all duration-75"
          style={{
            top: `${hoveredElement.rect.top}px`,
            left: `${hoveredElement.rect.left}px`,
            width: `${hoveredElement.rect.width}px`,
            height: `${hoveredElement.rect.height}px`
          }}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && confirmationPosition && (
        <div
          className="feedback-confirmation fixed z-[9999] transform -translate-x-1/2 animate-in fade-in slide-in-from-bottom-2 duration-200 px-4"
          style={{
            top: `${confirmationPosition.top}px`,
            left: `${confirmationPosition.left}px`,
            maxWidth: 'calc(100vw - 40px)'
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl border border-purple-200 p-4 w-[300px] max-w-full">
            <p className="text-sm font-medium text-gray-900 mb-3">
              Is this the element you want to give feedback on?
            </p>

            <div className="flex gap-2">
              <button
                onClick={confirmElementSelection}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:bg-purple-800 transition-colors text-sm font-medium"
              >
                <Check className="w-4 h-4" />
                Yes
              </button>

              <button
                onClick={reselectElement}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors text-sm font-medium"
                title="Select a different element"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              <button
                onClick={cancelSelectionMode}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors text-sm font-medium"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Mode Indicator */}
      {!showConfirmation && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[9999] pointer-events-none">
          <div className="bg-purple-600 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2">
            <MessageSquarePlus className="w-4 h-4" />
            Click any element to give feedback
            <button
              onClick={cancelSelectionMode}
              className="pointer-events-auto ml-2 p-1 hover:bg-purple-700 rounded-full transition-colors"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackSelectionOverlay;
